import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Box, Typography, Button, Stack, Skeleton, Checkbox, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
  useMediaQuery, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import './GenericTable.css';

const GenericTable = ({
  columns = [],
  data = [],
  renderActions = null,
  includeActions = true,
  rowClass = () => '',
  loading = false,
  defaultSort = { key: 'createdAt', direction: 'desc' },
  currentPage = 1,
  itemsPerPage = 10,
  setPage = () => {},
  onDeleteSelected = () => {},
  selectedRows = [],
  setSelectedRows = () => {},
  showDeleteButton = true,
  showCheckboxes = true,
  showResetSort = true,
  totalItems,
  displayMode,
  setDisplayMode
}) => {
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const isMobile = useMediaQuery('(max-width:768px)');
  const isCardMode = isMobile || displayMode === 'card';
  const loadMoreRef = useRef();

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
    setPage(1);
  };

  const handleResetSort = () => {
    setSortConfig(defaultSort);
    setPage(1);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const column = columns.find((col) => col.key === sortConfig.key);
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (column?.sortFunction) {
        return column.sortFunction(aValue, bValue) * (sortConfig.direction === 'asc' ? 1 : -1);
      }
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columns]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const visibleCardsData = useMemo(() => {
    return isCardMode ? sortedData.slice(0, visibleCount) : paginatedData;
  }, [sortedData, visibleCount, paginatedData, isCardMode]);

  const allSelected = visibleCardsData.every((row) => selectedRows.includes(row._id || row.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(visibleCardsData.map((row) => row._id || row.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const confirmDelete = () => setOpenConfirm(true);
  const cancelDelete = () => setOpenConfirm(false);
  const handleDelete = () => {
    onDeleteSelected(selectedRows);
    setSelectedRows([]);
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (!isCardMode) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && visibleCount < sortedData.length) {
        setVisibleCount((prev) => prev + 10);
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [isCardMode, sortedData.length, visibleCount]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" className="table-actions">
        {showResetSort && sortConfig.key && (
          <Button variant="outlined" size="small" onClick={handleResetSort}>
            Réinitialiser le tri
          </Button>
        )}
        <ToggleButtonGroup
          size="small"
          value={displayMode}
          exclusive
          onChange={(e, value) => value && setDisplayMode(value)}
        >
          <ToggleButton value="table"><ViewListIcon /></ToggleButton>
          <ToggleButton value="card"><ViewModuleIcon /></ToggleButton>
        </ToggleButtonGroup>
        {showDeleteButton && selectedRows.length > 0 && (
          <Button color="error" variant="contained" size="small" onClick={confirmDelete}>
            Supprimer la sélection ({selectedRows.length})
          </Button>
        )}
      </Stack>

      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
        <span className="total-count">
          Nombre total : <b>{totalItems}</b>
        </span>
      </Box>

      {isCardMode ? (
        <>
          <Box className="card-grid">
            {visibleCardsData.map((row) => {
              const id = row._id || row.id;
              return (
                <Paper key={id} className="cardRow">
                  <Box className="cardRowHeader">
                    <Typography fontWeight={600}>{row.nomCandidat || row.nom || 'Talent'}</Typography>
                    {showCheckboxes && (
                      <Checkbox
                        checked={selectedRows.includes(id)}
                        onChange={() => toggleSelectRow(id)}
                      />
                    )}
                  </Box>

                  {columns.map((col) => (
                    col.key !== 'nomCandidat' && (
                      <Box key={col.key} className="cardRowContent">
                        <Typography variant="body2" className="cardLabel">
                          {col.label}
                        </Typography>
                        <Typography variant="body2">
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </Typography>
                      </Box>
                    )
                  ))}

                  {includeActions && renderActions && (
                    <Box mt={2}>{renderActions(row)}</Box>
                  )}
                </Paper>
              );
            })}
          </Box>

          <Box display="flex" justifyContent="center" mt={2}>
            {visibleCount >= sortedData.length ? (
              <Typography variant="body2" color="text.secondary">Aucune autre donnée</Typography>
            ) : (
              <div ref={loadMoreRef} style={{ height: '30px' }} />
            )}
          </Box>
        </>
      ) : (
        <TableContainer component={Paper} className="modern-table">
          <Table>
            <TableHead>
              <TableRow>
                {showCheckboxes && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} onChange={toggleSelectAll} />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`table-header ${sortConfig.key === col.key ? 'active-sort' : ''}`}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" className="header-content">
                      {col.label}
                      {sortConfig.key === col.key && (
                        sortConfig.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                      )}
                    </Typography>
                  </TableCell>
                ))}
                {includeActions && renderActions && (
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">ACTIONS</Typography></TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell padding="checkbox"><Skeleton variant="rectangular" width={24} height={24} /></TableCell>
                    {columns.map((col) => (
                      <TableCell key={col.key}><Skeleton variant="text" width="80%" /></TableCell>
                    ))}
                    {includeActions && renderActions && (
                      <TableCell><Skeleton variant="rectangular" width={100} height={30} /></TableCell>
                    )}
                  </TableRow>
                ))
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} align="center">
                    <Typography>Aucune donnée trouvée.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => {
                  const id = row._id || row.id;
                  return (
                    <TableRow key={id} hover selected={selectedRows.includes(id)}>
                      {showCheckboxes && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedRows.includes(id)} onChange={() => toggleSelectRow(id)} />
                        </TableCell>
                      )}
                      {columns.map((col) => (
                        <TableCell key={col.key}>
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </TableCell>
                      ))}
                      {includeActions && renderActions && (
                        <TableCell>{renderActions(row)}</TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openConfirm} onClose={cancelDelete}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer {selectedRows.length} élément(s) ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GenericTable;
